// ── Leaflet 지도 초기화 + 마커 렌더링 + fitBounds 훅 ────────────────────────
// - 마커 아이콘 CSS 주입
// - 지도 초기화 (타일 레이어, 줌 컨트롤)
// - 드래그 가능 마커 렌더링 (리더선 + 실제 위치 핀 포함)
// - 시트 열릴 때 시트 영역을 고려한 fitBounds

import { useEffect } from 'react';
import L from 'leaflet';
import { createMeetingMarkerIcon } from '../../../components/meetingmap/MarkerIcons';
import { useMarkerConfig } from '../../../components/meetingmap/useMarkerConfig';
import {
  DEFAULT_CENTER, DEFAULT_ZOOM, MEETING_MARKER_ICON_CLASS,
} from '../../../components/meetingmap/utils';

export function useLeafletMap(core) {
  const {
    mapContainerRef, mapRef, layerRef, markersRef,
    shouldFitRef, draggedPositions, isDraggingMarkerRef, saveMarkerLayoutRef,
    leaderLayersRef, prevSheetOpenRef,
    sheetRef,
    validMeetings, selectedId, mapZoom, sheetOpen, selected, view, loading,
    externalCategories,
    setMapZoom, setSheetOpen, setSelectedId,
  } = core;
  // 마커 표시 설정(테넌트) — 오기 전·실패 시 처음 값, 설정 창에서 저장하면 바로 다시 그린다(2026-10-24)
  const markerConfig = useMarkerConfig();

  // ── 마커 아이콘 CSS 주입 ────────────────────────────────────────────────────
  useEffect(() => {
    const styleId = 'meeting-marker-icon-style';
    let style = document.getElementById(styleId);
    if (!style) {
      style = document.createElement('style');
      style.id = styleId;
      document.head.appendChild(style);
    }
    style.textContent = `
      .${MEETING_MARKER_ICON_CLASS} {
        background: transparent !important; border: none !important; box-shadow: none !important;
      }
      .${MEETING_MARKER_ICON_CLASS}.leaflet-marker-icon {
        background: transparent !important; border: none !important; box-shadow: none !important;
        border-radius: 999px !important; overflow: visible !important;
      }
    `;
  }, []);

  // ── 지도 초기화 ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;
    const map = L.map(mapContainerRef.current, { zoomControl: false, preferCanvas: true })
      .setView(DEFAULT_CENTER, DEFAULT_ZOOM);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors', maxZoom: 19,
    }).addTo(map);
    L.control.zoom({ position: 'bottomright' }).addTo(map);
    const layerGroup = L.layerGroup().addTo(map);
    // 줌 끝날 때마다 mapZoom 갱신 → 마커 클러스터 재계산 트리거
    map.on('zoomend', () => setMapZoom(map.getZoom()));
    // 지도 빈 공간 클릭 시 슬라이드 닫기 (마커 클릭은 stopPropagation으로 제외)
    map.on('click', () => setSheetOpen(false));
    mapRef.current = map;
    layerRef.current = layerGroup;
    return () => {
      map.remove();
      mapRef.current = null;
      layerRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── 드래그 가능 마커 렌더링 ─────────────────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    const layerGroup = layerRef.current;
    if (!map || !layerGroup) return;
    layerGroup.clearLayers();
    markersRef.current.clear();
    // 리더선·점은 map에 직접 추가했으므로 별도 정리
    leaderLayersRef.current.forEach((l) => { try { l.remove(); } catch (_) {} });
    leaderLayersRef.current = [];
    // SVG 렌더러 (preferCanvas:true 환경에서도 리더선을 SVG로 강제 렌더)
    const svgRenderer = L.svg();

    if (validMeetings.length === 0) { map.setView(DEFAULT_CENTER, DEFAULT_ZOOM); return; }

    // 최초 로드 / 날짜 변경 시 fitBounds(animate:false)로 줌 레벨 동기 확정
    if (shouldFitRef.current) {
      const bounds = L.latLngBounds(validMeetings.map((item) => [item._lat, item._lng]));
      map.fitBounds(bounds, { padding: [36, 36], maxZoom: 14, animate: false });
      shouldFitRef.current = false;
    }

    // 시트가 닫힐 때(open→close 전환): fitBounds로 전체 마커 뷰 리셋
    const wasOpen = prevSheetOpenRef.current;
    prevSheetOpenRef.current = sheetOpen;
    if (wasOpen && !sheetOpen) {
      const bounds = L.latLngBounds(validMeetings.map((item) => [item._lat, item._lng]));
      map.fitBounds(bounds, { padding: [36, 36], maxZoom: 14, animate: false });
    }

    validMeetings.forEach((item) => {
      const actualLatLng = L.latLng(item._lat, item._lng);
      // 이전에 드래그했던 위치가 있으면 그 위치에서 시작
      const savedPos = draggedPositions.current.get(Number(item.id));
      const startLatLng = savedPos ? L.latLng(savedPos.lat, savedPos.lng) : actualLatLng;
      const isSel = Number(item.id) === Number(selectedId);
      // extCatNames: 외부(비내부) 카테고리명 배열 — ticket_type 기반 마커 색상 판정
      const extCatNames = externalCategories.map((c) => c.name);
      const icon = createMeetingMarkerIcon(item, isSel, extCatNames, markerConfig);
      const marker = L.marker(startLatLng, { icon, draggable: true });

      let dotMarker = null;
      let leaderLine = null;

      // 실제 위치와 현재 표시 위치 간 거리가 있을 때 점+선 그리기
      // ※ preferCanvas:true 환경에서 canvas 렌더러는 polyline/circleMarker가 안 그려지므로
      //    L.svg() 렌더러를 명시하고 map에 직접 추가한 뒤 leaderLayersRef로 추적·정리함
      const updateLeader = (currentLatLng) => {
        const dist = actualLatLng.distanceTo(currentLatLng);
        if (dist < 5) {
          // 거의 원래 위치 → 점+선 제거
          if (dotMarker) {
            dotMarker.remove();
            leaderLayersRef.current = leaderLayersRef.current.filter((l) => l !== dotMarker);
            dotMarker = null;
          }
          if (leaderLine) {
            leaderLine.remove();
            leaderLayersRef.current = leaderLayersRef.current.filter((l) => l !== leaderLine);
            leaderLine = null;
          }
          return;
        }
        // 실제 위치 핀 — 유리구슬 입체형
        // ※ SVG gradient id가 DOM 전역 공유되므로 마커마다 고유 id 부여 (item.id 사용)
        if (!dotMarker) {
          const gid = `pgpin_${item.id}`;
          const pinSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="36" viewBox="0 0 24 36">
            <defs>
              <radialGradient id="${gid}a" cx="38%" cy="32%" r="60%">
                <stop offset="0%" stop-color="#ff6b8a"/>
                <stop offset="50%" stop-color="#e11d48"/>
                <stop offset="100%" stop-color="#9f0f2e"/>
              </radialGradient>
              <radialGradient id="${gid}b" cx="40%" cy="30%" r="55%">
                <stop offset="0%" stop-color="#fff" stop-opacity="0.7"/>
                <stop offset="100%" stop-color="#fff" stop-opacity="0"/>
              </radialGradient>
            </defs>
            <path d="M12 1C7.03 1 3 5.03 3 10c0 6.5 9 25 9 25s9-18.5 9-25c0-4.97-4.03-9-9-9z"
              fill="url(#${gid}a)" filter="drop-shadow(0 3px 5px rgba(0,0,0,0.35))"/>
            <ellipse cx="9" cy="7" rx="3.5" ry="2.5" fill="url(#${gid}b)"/>
          </svg>`;
          dotMarker = L.marker(actualLatLng, {
            icon: L.divIcon({
              className: '',
              html: pinSvg,
              iconSize: [24, 36],
              iconAnchor: [12, 35], // 핀 끝(y=35)이 actualLatLng를 정확히 가리킴
            }),
            interactive: false,
            zIndexOffset: -100,
          }).addTo(map);
          leaderLayersRef.current.push(dotMarker);
        }
        // 실제 위치 → 마커 현재 위치 연결선 (SVG 렌더러 강제)
        if (!leaderLine) {
          leaderLine = L.polyline([actualLatLng, currentLatLng], {
            color: '#1e40af', weight: 2.5, opacity: 0.9,
            dashArray: '6, 5', interactive: false, renderer: svgRenderer,
          }).addTo(map);
          leaderLayersRef.current.push(leaderLine);
        } else {
          leaderLine.setLatLngs([actualLatLng, currentLatLng]);
        }
      };

      // 드래그 중 플래그 — 당겨서-새로고침이 마커 드래그를 새로고침으로 오인하지 않게 차단
      marker.on('dragstart', () => { isDraggingMarkerRef.current = true; });
      marker.on('drag', (e) => { updateLeader(e.target.getLatLng()); });
      marker.on('dragend', (e) => {
        const pos = e.target.getLatLng();
        draggedPositions.current.set(Number(item.id), { lat: pos.lat, lng: pos.lng });
        updateLeader(pos);
        // 플래그 해제는 잠깐 늦게 — dragend 직후 도착하는 touchend/click 이 refresh 로 이어지지 않게
        setTimeout(() => { isDraggingMarkerRef.current = false; }, 250);
        // 배치 영속 저장 (디바운스 — handlers 가 주입)
        saveMarkerLayoutRef.current?.();
      });
      marker.on('click', (e) => {
        L.DomEvent.stopPropagation(e);
        setSelectedId(Number(item.id));
        setSheetOpen(true);
      });

      marker.addTo(layerGroup);
      markersRef.current.set(Number(item.id), marker);
      // 이미 드래그된 상태면 점+선 복원
      if (savedPos) updateLeader(startLatLng);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validMeetings, selectedId, mapZoom, sheetOpen, externalCategories, markerConfig]);

  // ── fitBounds (시트 영역 고려) ──────────────────────────────────────────────
  useEffect(() => {
    if (view !== 'map' || loading || !sheetOpen || !selected) return;
    const map = mapRef.current;
    if (!map || validMeetings.length === 0) return;
    let rafId = 0;
    rafId = window.requestAnimationFrame(() => {
      const size = map.getSize();
      if (!size || !size.x || !size.y) return;
      const sheetHeight = Math.max(0, Math.ceil(sheetRef.current?.getBoundingClientRect?.().height || 0));
      const padX = 16, padTop = 16;
      const padBottom = Math.max(24, sheetHeight + 14);
      const hasHiddenMarker = validMeetings.some((item) => {
        const point = map.latLngToContainerPoint([item._lat, item._lng]);
        return point.x < padX || point.x > size.x - padX || point.y < padTop || point.y > size.y - padBottom;
      });
      if (!hasHiddenMarker) return;
      const bounds = L.latLngBounds(validMeetings.map((item) => [item._lat, item._lng]));
      map.fitBounds(bounds, {
        paddingTopLeft: [padX, padTop],
        paddingBottomRight: [padX, padBottom],
        maxZoom: 14,
      });
    });
    return () => { if (rafId) window.cancelAnimationFrame(rafId); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, loading, sheetOpen, selected, validMeetings]);
}
